import { useQuery } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useEffect, useRef } from 'react';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { ContentLibraryItemArticle } from '@/components/contentLibrary/contentLibraryItemArticle';
import { ContentLibraryItemVideo } from '@/components/contentLibrary/contentLibraryItemVideo';
import { Header } from '@/components/header';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { getPublicContentLibraryItemV2QueryOptions } from '@/services/getPublicContentLibraryItemV2';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import {
  trackContentLibraryItemViewed,
  trackContentLibrarySharedItemRedirected,
} from '@/util/analyticsEvents';

const noop = () => {};

const JoinCta = ({ maxWidthClassName }: { maxWidthClassName: string }) => {
  const router = useRouter();

  return (
    <VStack
      className={twJoin(
        'mx-auto mb-10 w-full shrink-0 gap-3 rounded-xl border border-outline-100 bg-background-50 p-5',
        maxWidthClassName
      )}>
      <Text className="font-body font-semibold text-base text-typography-900">
        Primum connects community oncologists with expert specialists.
      </Text>
      <Text size="sm" className="text-typography-700">
        Sign in or create an account to like this item and browse the full Content Library.
      </Text>
      <HStack className="gap-3">
        <Button
          action="primary"
          size="md"
          onPress={() => router.push(ROUTES.LOGIN)}
          {...getAccessibilityProps({ label: 'Sign in', role: 'button' })}>
          <ButtonText>Sign in</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
};

const UnavailableState = () => (
  <VStack
    className="flex-1 items-center justify-center gap-3 px-8 py-24"
    accessibilityLiveRegion="polite">
    <Heading size="h2-md" className="text-center">
      This shared link is no longer available
    </Heading>
    <Text className="text-center text-typography-600">
      The item may have been unshared or removed. Ask whoever shared it for an updated link.
    </Text>
  </VStack>
);

/**
 * Static `noindex` head emitted on every render branch so shared items are not
 * search-indexable regardless of load state. Lifted to module scope to avoid
 * recreating the element on each render.
 */
const NOINDEX_HEAD = (
  <Head>
    <meta name="robots" content="noindex" />
  </Head>
);

/**
 * Public, read-only viewer for a Content Library item resolved from a share
 * token (`/content-library/shared/{token}`).
 *
 * Renders without authentication for anonymous visitors: the item's full article
 * or video content plus a "sign in" CTA, bypassing the item's own `isPublic` flag
 * and the usual auth-gated teaser since the token itself is the access grant. Likes
 * still require sign-in (`ContentLibraryItemFeedback` gates on auth unconditionally;
 * it has no `isPublic` input to bypass). Signed-in visitors are redirected to the
 * full authenticated item
 * (`/content-library/{id}`) so they get the complete experience with real like state;
 * a `CONTENT_LIBRARY_SHARED_ITEM_REDIRECTED` event fires here before the redirect and
 * the destination screen emits its own `CONTENT_LIBRARY_ITEM_VIEWED`. The `(open)`
 * layout exempts this path from its generic authenticated-user redirect so this
 * targeted redirect can resolve the item id first. A revoked or unknown token (404)
 * renders a dedicated "unavailable" state, and the page is `noindex` on web.
 */
export function PublicContentLibraryItem() {
  const { token, email } = useLocalSearchParams<{ token: string; email?: string }>();

  const router = useRouter();

  const { isAuthenticated } = useAuthStatus();

  const itemQuery = useQuery(getPublicContentLibraryItemV2QueryOptions({ token }));

  const handledItemIdRef = useRef<number | null>(null);

  useEffect(() => {
    const resolvedItem = itemQuery.data;

    if (!resolvedItem || handledItemIdRef.current === resolvedItem.id) {
      return;
    }

    handledItemIdRef.current = resolvedItem.id;

    if (isAuthenticated) {
      trackContentLibrarySharedItemRedirected({
        itemId: resolvedItem.id,
        email: email || undefined,
      });

      // Target the secure route directly so the (open) layout's authed-user
      // redirect doesn't re-fire and append the share token to the destination URL.
      router.replace(`/(secure)/(tabs)/(community)/content-library/${resolvedItem.id}`);

      return;
    }

    trackContentLibraryItemViewed({
      itemId: resolvedItem.id,
      source: 'shared_link',
      email: email || undefined,
    });
  }, [itemQuery.data, isAuthenticated, email, router]);

  // Signed-in users are mid-redirect to the authenticated item; hold the spinner
  // rather than flashing the teaser.
  const isRedirectingToFullItem = isAuthenticated && !!itemQuery.data;

  if (!token || itemQuery.isPending || isRedirectingToFullItem) {
    return (
      <>
        {NOINDEX_HEAD}
        <Header />
        <View
          className="py-24"
          accessibilityLiveRegion="polite"
          {...getAccessibilityProps({ label: 'Loading shared item', role: 'progressbar' })}>
          <Spinner size="large" />
        </View>
      </>
    );
  }

  if (itemQuery.isError || !itemQuery.data) {
    return (
      <>
        {NOINDEX_HEAD}
        <Header />
        <UnavailableState />
      </>
    );
  }

  const contentLibraryItem = itemQuery.data;

  const sharedItemProps = {
    changeLikeStatus: noop,
    contentLibraryItem,
    isPublic: true,
    likeStatus: null,
  };

  const isArticle = contentLibraryItem.type === 'Article';

  return (
    <>
      {NOINDEX_HEAD}
      {isArticle ? (
        <ContentLibraryItemArticle {...sharedItemProps} />
      ) : (
        <ContentLibraryItemVideo {...sharedItemProps} />
      )}
      <JoinCta maxWidthClassName={isArticle ? 'max-w-[850px]' : 'max-w-[1080px]'} />
    </>
  );
}
